import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { DocForeignHashJoinTable } from "building-types/doc/docForeignKeyTable";
import { docHashJoinTableFieldToValueEntityType } from "building-types/doc/docHashTableFieldsToForeignKeyRelations";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { isComponentOwnedMemberEntityHandle } from "building-types/handles/docEntityHandleTypes";
import objectFindKey from "common/utils/objectFindKey";
import { isRoomLayoutEligibleDocEntityHandle } from "doc/rooms/layout/roomLayoutEligibleEntityTypes";
import { isGroupableEntityHandle } from "./entity/docEntityGroupable";

export type DocHashJoinTableName = keyof typeof docHashJoinTableFieldToValueEntityType;

const docJoinHashTableNames = Object.keys(docHashJoinTableFieldToValueEntityType) as DocHashJoinTableName[];

export function docForEachHashJoinTable(
  doc: Doc,
  callback: (docTableName: DocHashJoinTableName, table: Doc[DocHashJoinTableName]) => void,
) {
  for (const docTableName of docJoinHashTableNames) {
    const table = doc[docTableName];
    callback(docTableName, table);
  }
}

export function docEntityTypeToHashJoinTableName(entityType: DocEntityType): DocHashJoinTableName | undefined {
  return objectFindKey(docHashJoinTableFieldToValueEntityType, (type) => type === entityType) as
    | DocHashJoinTableName
    | undefined;
}

export const docHashJoinTableFieldToMemberEligibilityCheck: {
  [key in DocHashJoinTableName]: (handle: DocEntityHandle) => boolean;
} = {
  componentMembers: isComponentOwnedMemberEntityHandle,
  entityGroups: isGroupableEntityHandle,
  entityRoomLayouts: isRoomLayoutEligibleDocEntityHandle,
};

export function isDocHashJoinTableName(fieldPath: string): fieldPath is DocHashJoinTableName {
  return fieldPath in docHashJoinTableFieldToValueEntityType;
}

/**
 * Inverts a hash-join table (hash -> foreign id) into (foreign id -> hashes)
 */
export function buildValueToHashesIndex(table: DocForeignHashJoinTable<DocEntityType, string>): Map<string, string[]> {
  const index = new Map<string, string[]>();

  for (const hash in table) {
    const value = table[hash];
    const hashes = index.get(value);
    if (hashes) {
      hashes.push(hash);
    } else {
      index.set(value, [hash]);
    }
  }

  return index;
}
